// AbMapPositionsTab — Positions-Erkennung: Strategie (column_rows | indented_position_blocks)
// + deren Parameter (Zeilen-Anker/x-Band bzw. Start-/Bedingungs-Muster + Lieferungs-Split) +
// gemeinsame "Positionen ueberspringen"-Liste (row_skip). Vorbild: OC-Pilot MappingView.tsx
// Tab "Positionen" (NUR gelesen).

import React from 'react';
import { X } from 'lucide-react';
import { inputCls } from '../components/ui';
import AbMapPickButton from './AbMapPickButton';
import { DEFAULT_ANCHOR, DEFAULT_START } from './rulesetModel';

const ChipList = ({ isDarkMode, items, onRemove, onAdd, placeholder }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((d, i) => (
      <span key={`${d}-${i}`} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md ${isDarkMode ? 'bg-white/10 text-white/75' : 'bg-slate-100 text-slate-700'}`}>
        <span className="font-mono">{d}</span>
        <button type="button" onClick={() => onRemove(i)} className={isDarkMode ? 'text-white/40 hover:text-white' : 'text-slate-400 hover:text-slate-700'}>
          <X className="w-3 h-3" />
        </button>
      </span>
    ))}
    <input
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const v = e.target.value.trim();
        if (v) { onAdd(v); e.target.value = ''; }
      }}
      className={`${inputCls(isDarkMode)} text-xs px-2 py-1 min-w-[200px] font-mono`}
    />
  </div>
);

const AbMapPositionsTab = ({ isDarkMode, t, form, onChange, pick, onPick }) => {
  const textMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';
  const labelCls = `text-xs font-medium ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`;
  const sectionTitle = `text-sm font-semibold ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`;
  const isColumnRows = form.strategy === 'column_rows';
  const isPicking = (kind) => pick && pick.kind === kind && !pick.field;

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className={sectionTitle}>{t('ab.map.tableTitle') /* Ueberschrift generisch fuer beide Strategien */}</h3>
        <label className={labelCls}>{t('ab.map.strategyLabel')}</label>
        <select
          value={form.strategy}
          onChange={(e) => onChange({ strategy: e.target.value })}
          className={`${inputCls(isDarkMode)} w-full`}
        >
          <option value="column_rows">{t('ab.map.strategy.column_rows')}</option>
          <option value="blocks">{t('ab.map.strategy.blocks')}</option>
        </select>
        <p className={`text-xs ${textMuted}`}>{isColumnRows ? t('ab.map.strategyHintColumns') : t('ab.map.strategyHintBlocks')}</p>
      </section>

      {isColumnRows && (
        <section className="space-y-3">
          <h3 className={sectionTitle}>{t('ab.map.tableTitle')}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <AbMapPickButton isDarkMode={isDarkMode} active={isPicking('row-anchor')} label={t('ab.map.rowAnchorPick')} onClick={() => onPick({ kind: 'row-anchor' })} />
            {form.rowAnchorXMin != null
              ? <code className={`text-xs ${textMuted}`}>x {form.rowAnchorXMin}–{form.rowAnchorXMax} · {t('ab.map.pageLabel')} {form.page}</code>
              : <span className={`text-xs ${textMuted}`}>{t('ab.map.rowAnchorHint')}</span>}
          </div>
          <div>
            <label className={labelCls}>{t('ab.map.rowAnchorPatternLabel')}</label>
            <input value={form.rowAnchorPattern} placeholder={DEFAULT_ANCHOR} onChange={(e) => onChange({ rowAnchorPattern: e.target.value })} className={`${inputCls(isDarkMode)} w-full mt-1 font-mono text-xs`} />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.allPages} onChange={(e) => onChange({ allPages: e.target.checked })} />
            <span className={isDarkMode ? 'text-white/70' : 'text-slate-600'}>{t('ab.map.allPagesLabel')}</span>
          </label>
          <div>
            <label className={labelCls}>{t('ab.map.bandPadLabel')}</label>
            <input type="number" min={0} max={30} value={form.bandPad} onChange={(e) => onChange({ bandPad: Number(e.target.value) || 0 })} className={`${inputCls(isDarkMode)} w-24 mt-1`} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={labelCls}>{t('ab.map.rowEndLabel')}:</span>
            {form.rowEnd ? <code className={`text-xs ${textMuted}`}>{form.rowEnd}</code> : <span className={`text-xs ${textMuted}`}>{t('ab.map.rowEndNone')}</span>}
            <AbMapPickButton isDarkMode={isDarkMode} active={isPicking('row-end')} label={t('ab.map.rowEndPick')} onClick={() => onPick({ kind: 'row-end' })} />
            {form.rowEnd && (
              <button type="button" onClick={() => onChange({ rowEnd: null })} className={`text-xs underline ${textMuted}`}>{t('ab.map.rowEndRemove')}</button>
            )}
          </div>
          <p className={`text-xs ${textMuted}`}>{t('ab.map.rowEndHint')}</p>
        </section>
      )}

      {!isColumnRows && (
        <>
          <section className="space-y-3">
            <h3 className={sectionTitle}>{t('ab.map.blocksTitle')}</h3>
            <div>
              <label className={labelCls}>{t('ab.map.startPatternLabel')}</label>
              <input value={form.startPattern} placeholder={DEFAULT_START} onChange={(e) => onChange({ startPattern: e.target.value })} className={`${inputCls(isDarkMode)} w-full mt-1 font-mono text-xs`} />
            </div>
            <div>
              <label className={labelCls}>{t('ab.map.requirePatternLabel')}</label>
              <input value={form.requirePattern} placeholder={t('ab.map.requirePatternPlaceholder')} onChange={(e) => onChange({ requirePattern: e.target.value })} className={`${inputCls(isDarkMode)} w-full mt-1 font-mono text-xs`} />
            </div>
            <p className={`text-xs ${textMuted}`}>{t('ab.map.blocksHint')}</p>
          </section>

          <section className="space-y-3">
            <h3 className={sectionTitle}>{t('ab.map.deliveryTitle')}</h3>
            <p className={`text-xs ${textMuted}`}>{t('ab.map.deliveryHint')}</p>
            <div>
              <label className={labelCls}>{t('ab.map.deliveryAnchorLabel')}</label>
              <input value={form.deliveryAnchor} placeholder={t('ab.map.deliveryAnchorPlaceholder')} onChange={(e) => onChange({ deliveryAnchor: e.target.value })} className={`${inputCls(isDarkMode)} w-full mt-1`} />
            </div>
            <div>
              <label className={labelCls}>{t('ab.map.deliveryPairIndexLabel')}</label>
              <input type="number" min={0} max={20} value={form.deliveryPairIndex} onChange={(e) => onChange({ deliveryPairIndex: Number(e.target.value) || 0 })} className={`${inputCls(isDarkMode)} w-24 mt-1`} />
            </div>
            <details>
              <summary className={`text-xs cursor-pointer ${textMuted}`}>{t('ab.map.deliveryAdvanced')}</summary>
              <div className="mt-2 space-y-2">
                <div>
                  <label className={labelCls}>{t('ab.map.deliveryDatePatternLabel')}</label>
                  <input value={form.deliveryDatePattern} placeholder={t('ab.map.patternDefaultPlaceholder')} onChange={(e) => onChange({ deliveryDatePattern: e.target.value })} className={`${inputCls(isDarkMode)} w-full mt-1 font-mono text-xs`} />
                </div>
                <div>
                  <label className={labelCls}>{t('ab.map.deliveryNumPatternLabel')}</label>
                  <input value={form.deliveryNumPattern} placeholder={t('ab.map.patternDefaultPlaceholder')} onChange={(e) => onChange({ deliveryNumPattern: e.target.value })} className={`${inputCls(isDarkMode)} w-full mt-1 font-mono text-xs`} />
                </div>
                <div>
                  <label className={labelCls}>{t('ab.map.deliveryStopPrefixLabel')}</label>
                  <input value={form.deliveryStopPrefix} onChange={(e) => onChange({ deliveryStopPrefix: e.target.value })} className={`${inputCls(isDarkMode)} w-full mt-1`} />
                </div>
              </div>
            </details>
          </section>
        </>
      )}

      {isColumnRows && form.fields.EEIND.format === 'isoweek_week_column' && (
        <section className="space-y-2">
          <label className={labelCls}>{t('ab.map.yearPatternLabel')}</label>
          <input value={form.yearPattern} onChange={(e) => onChange({ yearPattern: e.target.value })} className={`${inputCls(isDarkMode)} w-full mt-1 font-mono text-xs`} />
        </section>
      )}

      <section className="space-y-2">
        <h3 className={sectionTitle}>
          {t('ab.map.rowSkipTitle')} <small className={textMuted}>— {t('ab.map.rowSkipSubtitle')}</small>
        </h3>
        <ChipList
          isDarkMode={isDarkMode} items={form.rowSkip} placeholder={t('ab.map.rowSkipPlaceholder')}
          onRemove={(i) => onChange({ rowSkip: form.rowSkip.filter((_, j) => j !== i) })}
          onAdd={(v) => onChange({ rowSkip: [...form.rowSkip, v] })}
        />
        <p className={`text-xs ${textMuted}`}>{t('ab.map.rowSkipHint')}</p>
      </section>
    </div>
  );
};

export default AbMapPositionsTab;
